import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Device } from "@services/devices/types";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import { useErrorNotification } from "@common/components/Notification";
import { getAggregatedDevices } from "@services/devices";
import { DeviceProvisioningDispatchContext } from "@pages/Assets/DeviceProvisioning/context";

type AggregatedContextType =
  | {
      devices: Device[] | undefined;
      setDevices: React.Dispatch<React.SetStateAction<Device[] | undefined>>;
      refetch: () => Promise<void>;
    }
  | undefined;

export const AggregatedContext =
  createContext<AggregatedContextType>(undefined);

export const AggregatedDispatchContext = createContext<
  AggregatedContextType | undefined
>(undefined);

const sortFn = (a: Device, b: Device) => {
  if (Boolean(a.status) !== Boolean(b.status))
    return Boolean(a.status) ? -1 : 1;
  return (a.name ?? "").localeCompare(b.name ?? "");
};
export const AggregatedContextProvider = ({ children }: PropsWithChildren) => {
  const { detail, showProvisionedOnly } = useContext(RoomsRealtimeContext);
  const { setDevices: setProvisioningDevices } = useContext(
    DeviceProvisioningDispatchContext
  );
  const dispatchError = useErrorNotification();
  const [devices, setDevices] = useState<Device[] | undefined>(undefined);
  const device = detail?.device;

  const fetch = useCallback(async () => {
    if (!device?.id) return;
    setDevices(undefined);
    try {
      const devices = (
        await getAggregatedDevices(device.id, showProvisionedOnly ?? false)
      ).list;
      const sortedDevices = devices.sort(sortFn);
      setDevices(sortedDevices);
      setProvisioningDevices(
        sortedDevices.filter(({ provisioned }) => !provisioned)
      );
    } catch (e) {
      dispatchError(e);
    }
  }, [device?.id, showProvisionedOnly, setProvisioningDevices]);

  useEffect(() => {
    if (device?.isAggregator && device.id) {
      fetch();
    }
  }, [device?.isAggregator, device?.id, showProvisionedOnly, fetch]);

  const value = useMemo<AggregatedContextType>(() => {
    if (!device?.isAggregator || !device?.id) return undefined;

    return {
      devices,
      setDevices,
      refetch: fetch,
    };
  }, [device?.isAggregator, device?.id, devices, setDevices, fetch]);
  return (
    <AggregatedContext.Provider value={value}>
      {children}
    </AggregatedContext.Provider>
  );
};
